import {
  Component,
  Element,
  Event,
  EventEmitter,
  forceUpdate,
  h,
  Host,
  Listen,
  Method,
  Prop,
  Watch,
} from '@stencil/core';

import { LhTexttoggleItemDataV1 } from '@/src/components/texttoggle-v1/patternlib-texttoggle-item-v1/patternlib-texttoggle-item-v1';
import { syntheticHtmlElementID } from '@/src/utils/id/synteticHtmlElementId';
import { ClassNames } from '@/src/utils/interfaces';
import { dumpComponentState } from '@/src/utils/utils';

export type sizeType = 'big' | 'medium' | 'small' | 'extra-small' | 'L' | 'M' | 'S' | 'XS';

export interface LhTexttoggleStateV1 {
  disabled: boolean;
  items: LhTexttoggleItemDataV1[];
  selectedItem: LhTexttoggleItemDataV1 | null;
  size: sizeType; // 48px, 40px, 32px, 24px
}

@Component({
  tag: 'patternlib-texttoggle-v1',
  styleUrl: 'patternlib-texttoggle-v1.scss',
  shadow: true,
})
export class PatternlibTexttoggleV1 {
  private _inputId = '';
  public internalState: LhTexttoggleStateV1 = {
    disabled: false,
    items: [],
    selectedItem: null,
    size: 'big',
  };

  @Element() hostElement: HTMLPatternlibTexttoggleV1Element;

  /**
   * Set the form group's additional information.
   */
  @Prop() additionalInfo = '';

  /**
   * Set the position of the additional info.
   */
  @Prop() additionalInfoPosition: 'top' | 'bottom' = 'top';

  /**
   * Make the component controllable (inspired by react).
   */
  @Prop() controlled = false;

  /** @internal */
  @Prop() debug = false;

  /** @internal */
  @Prop() debugInfo = '';

  /**
   * Set the components disabled state.
   */
  @Prop() disabled = false;

  /**
   * Set the id attribute of the input element.
   * The id must be unique in the document!
   * If not set, a random id is generated.
   */
  @Prop() inputId = '';

  /**
   * Set the Texttoggle label.
   */
  @Prop() label = '';

  /**
   * Set the Texttoggle name.
   */
  @Prop() name = '';

  /**
   * Reserve fix space for label area (label and hint).
   */
  @Prop() placeholderSpaceForLabelArea = false;

  /**
   * Reserve flexible space for status lines. Since status can have multiple lines, this property reserves space for them.
   */
  @Prop() placeholderSpaceForStatusLines = 0;

  /**
   * The height in T-shirt sizes: 'L' (48px), 'M' (40px), 'S' (32px) and 'XS' (24px).
   */
  @Prop() size: 'L' | 'M' | 'S' | 'XS' = 'L';

  /**
   * Define the components status.
   */
  @Prop() status: 'info' | 'error' | 'success' | 'warning' | '' = '';

  /**
   * Define the components status text.
   */
  @Prop() statusText = '';

  /**
   * Get or set the current value. Must be set as a texttoggle requires one selected item from the start.
   */
  @Prop({ mutable: true }) value = '';

  /**
   * Set the WCAG level. Default is AAA. When set to AA, the pointer target size is collapsed (no extra area).
   */
  @Prop() wcag: 'AA' | 'AAA' = 'AAA';

  /**
   * Event for the input text change. Event detail is the value and the name of the control.
   */
  @Event({ eventName: 'lhChange' }) changeEvent: EventEmitter<{ value: string; name: string }>;

  /** @internal */
  @Method()
  async getInternalState(): Promise<LhTexttoggleStateV1> {
    return Promise.resolve(this.internalState);
  }

  @Listen('lhItemSelect')
  openTextToggle(e: CustomEvent<LhTexttoggleItemDataV1>): void {
    e.stopPropagation();

    if (e.detail == null) {
      console.warn('[patternlib-texttoggle-v1] Item select event has no detail.');
      return;
    }

    this.preSelectItem(e.detail);
  }

  @Watch('disabled')
  handleDisabled(newValue: boolean): void {
    this.setStateDisabled(newValue);
  }

  @Watch('size')
  sizeWatcher(newValue: sizeType): void {
    this.setStateSize(newValue);
  }

  // Signal value changs from outside or from inside
  @Watch('value')
  valueWatcher(newValue: string): void {
    if (newValue == null) {
      console.warn('[patternlib-texttoggle-v1] Value is null/undefined. This should not happen.');
      if (!this.controlled) {
        this.value = '';
        newValue = '';
      }
    }

    this.handleWatchValue(newValue);
  }

  componentWillLoad(): void {
    if (this.value == null) {
      console.warn('[patternlib-texttoggle-v1] Value is null/undefined. This should not happen.');
      if (!this.controlled) {
        this.value = '';
      }
    }

    // State
    this.setStateSize(this.size);
    this.setStateDisabled(this.disabled);

    // Html element id (do not use Nullish Coalescing Operator here)
    this._inputId = this.inputId === '' ? syntheticHtmlElementID() : this.inputId;
  }

  componentDidLoad(): void {
    this.setStateCollectItemsFromLightDOM();
    this.handleWatchValue(this.value);
  }

  componentWillRender(): void {
    const selectedValue: string | undefined = this.internalState.selectedItem?.element.value;

    this.internalState.items.forEach(x => {
      x.element.debug = this.debug;
      x.element.disabled = this.internalState.disabled;
      x.element.ptsExpansions = this.wcag === 'AAA';
      x.element.size = this.internalState.size;
      x.element.selected = x.element.value === selectedValue;
    });
  }

  // eslint-disable-next-line complexity
  render() {
    // Slots
    const hasErrorSlot = this.hostElement.querySelector('[slot="error-message"]') != null;
    const hasSuccessSlot = this.hostElement.querySelector('[slot="success-message"]') != null;
    const hasWarningSlot = this.hostElement.querySelector('[slot="warning-message"]') != null;
    const hasInfoSlot = this.hostElement.querySelector('[slot="info-message"]') != null;

    // Map status to form-group status
    let _formGroupStatus: 'error' | 'success' | 'warning' | 'default' | '' = '';
    let _formGroupStatusText = '';
    if (this.status === 'error' || hasErrorSlot) {
      _formGroupStatus = 'error';
      _formGroupStatusText = this.statusText;
    } else if (this.status === 'success' || hasSuccessSlot) {
      _formGroupStatus = 'success';
      _formGroupStatusText = this.statusText;
    } else if (this.status === 'warning' || hasWarningSlot) {
      _formGroupStatus = 'warning';
      _formGroupStatusText = this.statusText;
    } else if (this.status === 'info' || hasInfoSlot) {
      _formGroupStatus = 'default';
      _formGroupStatusText = this.statusText;
    }

    const hostStyle = {
      userSelect: 'none',
      pointerEvents: this.disabled ? 'none' : null,
    };

    const componenteElementClasses: ClassNames = {
      'component-element': true,
      'disabled': this.disabled,
      [this.internalState.size]: true,
    };

    return (
      <Host
        role="radiogroup"
        aria-disabled={this.disabled ? 'true' : 'false'}
        aria-label={this.label}
        style={hostStyle}
      >
        <div class="component-wrapper">
          <patternlib-form-group
            additionalInfoPosition={this.additionalInfoPosition}
            data-testid="pl-form-group"
            labelId={this._inputId}
            reserveSpaceForLabelArea={this.placeholderSpaceForLabelArea}
            reserveSpaceForStatusLines={this.placeholderSpaceForStatusLines}
            status={_formGroupStatus}
          >
            {this.label && <span slot="label">{this.label}</span>}
            {this.additionalInfo && <span slot="additional-info">{this.additionalInfo}</span>}

            <div class={componenteElementClasses} onKeyDown={this.handleKeyDown} slot="element">
              <slot onSlotchange={() => this.handleItemsSlotChange()}></slot>
            </div>

            {_formGroupStatus === 'error' && _formGroupStatusText !== '' && (
              <div slot="error-message">{_formGroupStatusText}</div>
            )}
            {_formGroupStatus === 'default' && _formGroupStatusText !== '' && (
              <div slot="info-message">{_formGroupStatusText}</div>
            )}
            {_formGroupStatus === 'warning' && _formGroupStatusText !== '' && (
              <div slot="warning-message">{_formGroupStatusText}</div>
            )}
            {_formGroupStatus === 'success' && _formGroupStatusText !== '' && (
              <div slot="success-message">{_formGroupStatusText}</div>
            )}

            {hasErrorSlot && <slot name="error-message" slot="error-message"></slot>}
            {hasInfoSlot && <slot name="info-message" slot="info-message"></slot>}
            {hasSuccessSlot && <slot name="success-message" slot="success-message"></slot>}
            {hasWarningSlot && <slot name="warning-message" slot="warning-message"></slot>}
          </patternlib-form-group>
        </div>
      </Host>
    );
  }

  private setStateCollectItemsFromLightDOM(): void {
    this.internalState.items = [];
    Array.from(this.hostElement.querySelectorAll('patternlib-texttoggle-item-v1')).map(
      (el: HTMLPatternlibTexttoggleItemV1Element): void => {
        this.internalState.items.push({
          selected: el.selected,
          disabled: el.disabled,
          element: el,
        });
      },
    );
    this.dumpState('setStateCollectItems');
  }

  private setStateSelectedItem(item: LhTexttoggleItemDataV1 | null): void {
    this.internalState.selectedItem = item;
    forceUpdate(this);
    this.dumpState('setStateSelectedItem');
  }

  private setStateDisabled(disabled: boolean): void {
    this.internalState.disabled = disabled;
    this.dumpState('setStateDisabled');
  }

  private setStateSize(size: sizeType): void {
    this.internalState.size = size;
    this.dumpState('setStateSize');
  }

  private handleKeyDown = (evt: KeyboardEvent): void => {
    if (evt.key === 'ArrowLeft' || evt.key === 'ArrowRight') {
      this.handleKeyLeftRight(evt);
      return;
    }
  };

  private handleKeyLeftRight(e: KeyboardEvent): void {
    e.stopPropagation();

    let currIndex = 0;
    this.internalState.items.forEach((element, idx) => {
      if (element.element.selected) {
        currIndex = idx;
      }
    });

    let nextIndex = currIndex;
    if (e.key === 'ArrowLeft') {
      nextIndex--;
      if (nextIndex < 0) {
        nextIndex = this.internalState.items.length - 1;
      }
    }
    if (e.key === 'ArrowRight') {
      nextIndex++;
      if (nextIndex > this.internalState.items.length - 1) {
        nextIndex = 0;
      }
    }

    // Set item (including focus)
    this.preSelectItem(this.internalState.items[nextIndex]);
    this.internalState.items[nextIndex].element.setFocus();
  }

  private handleWatchValue(value: string): void {
    // Invalid value case
    if (!value) {
      return;
    }

    // Items from light dom not yet collected
    if (this.internalState.items.length === 0) {
      return;
    }

    // Find the matching item
    const matchingItem = this.internalState.items.find(item => item.element.value === value) || null;
    if (matchingItem && this.internalState.selectedItem?.element.value !== matchingItem.element.value) {
      this.setStateSelectedItem(matchingItem);
    }
  }

  private preSelectItem(item: LhTexttoggleItemDataV1): void {
    // Item already selected
    if (
      this.internalState.selectedItem != null &&
      this.internalState.selectedItem.element.value === item.element.value
    ) {
      return;
    }

    // Trigger item select
    if (!this.controlled) {
      this.value = item.element.value;
    }

    this.changeEvent.emit({ value: item.element.value, name: this.name });
  }

  private handleItemsSlotChange(): void {
    this.setStateCollectItemsFromLightDOM();

    // when options from light dom comes after value is set.
    if (this.value) {
      this.handleWatchValue(this.value);
    }
  }

  private dumpState(stateChangeSource = ''): void {
    if (this.debug) {
      dumpComponentState('TexttoggleV1', this.internalState, stateChangeSource, this.debugInfo);
    }
  }
}
